import { Box, Flex, Text, VStack } from '@chakra-ui/react'
import { Pencil, Plus, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useModalState } from '~/atoms/ModalState'
import { useSettingsState } from '~/atoms/SettingsState'
import { useUserState } from '~/atoms/UserState'
import { QuickActionButtonVariants } from '~/components/shared/componentStyles/QuickActionButton.style'
import { TextButtonVariants } from '~/components/shared/componentStyles/TextButton.style'
import { QuickActionButton } from '~/components/shared/QuickActionButton'
import { TextButton } from '~/components/shared/TextButton'
import { useQueueAlerts } from '~/hooks/useQueueToasts'
import { CustomStatusModal } from '~/pages/Home/Modals/CustomStatusModal/CustomStatusModal'
import {
  useDeleteCustomStatus,
  useGetCustomStatuses,
} from '~/queries/customStatusQueries'
import { useQueueLogout } from '~/queries/queueQueries'
import Logger from '~/services/Logger'
import { Availability } from '~/types/AgentCustomStatus'
import { Size } from '~/types/ThemeTypes'
import { isUserAdmin } from '~/utils/auth'
import { useMultiStyleConfig } from '~/utils/styleHelpers'
import { showSuccessToast } from '~/utils/toast'

import { textStyleKeys } from '@mangovoice/design-system'
import { textStyles } from '@mangovoice/design-system'
import { ApiError } from '@mangovoice/types'

import { CallCenterSettingsStyle } from './componentStyles/CallCenterSettings.style'
import { SettingToggleDependentSection } from './SettingItems'
import { DefaultSettingsWrapper, SubheadingSection } from './Shared'

const logger = new Logger('CallCenterSettings')

interface CallCenterSettingsProps {
  pbxId: number | undefined
  extensionId: number | undefined
}
export const CallCenterSettings = ({
  pbxId,
  extensionId,
}: CallCenterSettingsProps) => {
  const { t } = useTranslation()
  const {
    settings: {
      callCenter: { isEnabled: isCallCenterModeEnabled },
    },
    setCallCenterAgentEnabled,
    setDoNotDisturb,
  } = useSettingsState(pbxId)
  const { userRoles } = useUserState()

  const queueLogout = useQueueLogout({ extensionId })
  const { showQueueUpdateGenericErrorModal } = useQueueAlerts()

  const { data: customStatuses, refetch: refetchStatuses } =
    useGetCustomStatuses(pbxId)
  const { mutate: deleteCustomStatus } = useDeleteCustomStatus(() => {
    showSuccessToast({
      message: t('callCenterSettings.toast.deleteStatusSuccess'),
    })
  })

  const handleCallCenterModeToggle = () => {
    const newCallCenterMode = !isCallCenterModeEnabled
    if (!newCallCenterMode) {
      queueLogout().catch((error: unknown) => {
        // 404 = extension isn't in any call queue, so there is nothing to
        // log out of.
        if ((error as ApiError)?.status === 404) {
          logger.info(
            'Queue logout skipped on call center mode disable: extension not in a call queue',
          )
          return
        }
        logger.error('Queue logout failed on call center mode disable', null, {
          error,
        })
        showQueueUpdateGenericErrorModal()
      })
    } else {
      setDoNotDisturb(undefined)
    }
    setCallCenterAgentEnabled(newCallCenterMode)
  }

  const {
    customStatusModal: [, setCustomStatusModalState],
    confirmModal: [, setConfirmModalState],
  } = useModalState()

  const handleEditStatus = (status: {
    id: string
    name: string
    description: string
    availability: Availability
  }) => {
    setCustomStatusModalState({
      open: true,
      customStatus: {
        id: status.id,
        name: status.name,
        description: status.description,
        availability: status.availability,
      },
    })
  }

  const handleNewStatus = () => {
    setCustomStatusModalState({
      open: true,
    })
  }

  const styles = useMultiStyleConfig(CallCenterSettingsStyle)

  return (
    <>
      <DefaultSettingsWrapper>
        <SubheadingSection id="agent-mode">
          <Box width="100%" css={{ '& > *': { width: '100%' } }}>
            <SettingToggleDependentSection
              label={t('callCenterSettings.enableAgentMode')}
              isChecked={isCallCenterModeEnabled}
              onClick={handleCallCenterModeToggle}
              toggleOnContent={
                <VStack w="full" gap="1rem">
                  {isUserAdmin(userRoles) && (
                    <SubheadingSection id="custom-statuses">
                      <Flex css={styles.customStatusHeaderContainer}>
                        <Flex css={styles.customStatusHeaderContent}>
                          <Text
                            {...textStyles[textStyleKeys.Body2_Bold]}
                            color="fg.default"
                          >
                            {t('callCenterSettings.customStatuses')}
                          </Text>
                          <Text textStyle="Body2" color="fg.muted">
                            {t('callCenterSettings.customStatusesDescription')}
                          </Text>
                        </Flex>
                        <Flex css={styles.customStatusWrapper}>
                          {customStatuses?.map((status) => {
                            return (
                              <Flex
                                css={styles.customStatusContainer}
                                key={status.id}
                              >
                                <Flex css={styles.customStatusContent}>
                                  <Flex alignItems="center" gap="0.25rem">
                                    <Flex
                                      {...styles.statusIndicator}
                                      background={
                                        status.availability === 'Away'
                                          ? 'orange.solid'
                                          : 'green.solid'
                                      }
                                    />
                                    <Text
                                      css={styles.statusTitle}
                                      color="fg.default"
                                    >
                                      {status.name}
                                    </Text>
                                  </Flex>
                                  <Text
                                    css={styles.statusDescription}
                                    color="fg.default"
                                  >
                                    {status.description}
                                  </Text>
                                </Flex>
                                <Flex css={styles.actionButtons}>
                                  <QuickActionButton
                                    variant={
                                      QuickActionButtonVariants.Default_100
                                    }
                                    actionIcon={Pencil}
                                    tooltipText={t(
                                      'callCenterSettings.editStatus',
                                    )}
                                    size={Size.sm}
                                    onClick={() => handleEditStatus(status)}
                                  />
                                  <QuickActionButton
                                    variant={
                                      QuickActionButtonVariants.Destructive_100
                                    }
                                    actionIcon={Trash2}
                                    tooltipText={t(
                                      'callCenterSettings.deleteStatus',
                                    )}
                                    size={Size.sm}
                                    iconProps={{
                                      color: 'fg.default',
                                      _groupHover: { color: 'red.solid' },
                                    }}
                                    onClick={() => {
                                      setConfirmModalState({
                                        title: t(
                                          'callCenterSettings.confirmationModal.title',
                                        ),
                                        message: (
                                          <Text>
                                            {t(
                                              'callCenterSettings.confirmationModal.text1',
                                            )}
                                            <Text as="span" fontWeight="bold">
                                              {status.name}
                                            </Text>
                                            {t(
                                              'callCenterSettings.confirmationModal.text2',
                                            )}
                                          </Text>
                                        ),
                                        confirmText: t(
                                          'callCenterSettings.confirmationModal.confirmText',
                                        ),
                                        cancelText: t(
                                          'callCenterSettings.confirmationModal.cancelText',
                                        ),
                                        closeAfterConfirm: true,
                                        isDestructive: true,
                                        onConfirm: () => {
                                          deleteCustomStatus(status.id)
                                        },
                                      })
                                    }}
                                  />
                                </Flex>
                              </Flex>
                            )
                          })}
                          <TextButton
                            leftIcon={Plus}
                            onLinkClick={handleNewStatus}
                            variant={TextButtonVariants.Info}
                            leftIconProps={{
                              boxSize: '10px',
                              position: 'relative',
                              bottom: '.5px',
                              stroke: 'inherit',
                            }}
                            iconStrokeWidth="2px"
                          >
                            {t('callCenterSettings.newStatus')}
                          </TextButton>
                        </Flex>
                      </Flex>
                    </SubheadingSection>
                  )}
                </VStack>
              }
            />
          </Box>
        </SubheadingSection>
      </DefaultSettingsWrapper>
      <CustomStatusModal onCreated={refetchStatuses} />
    </>
  )
}
